import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Modal } from "@/components/Modal.jsx";
import { Flame, ExternalLink, AlertTriangle } from "lucide-react";
import { useAuthContext } from "@/wrappers/AuthWrapper.jsx";
import { getv, trim2, tofeth, nils } from "@/utils/utils.jsx";
import _ from "lodash";
import DNA_SkinsMaster from "@/contracts/v2/DNA_SkinsMaster.js";
import DNA_SkinsBurner from "@/contracts/v2/DNA_SkinsBurner.js";
import ERC721base from "@/contracts/v2/ERC721base";
import { contractAddress_list } from "@/contracts/contracts.jsx";
import { skins_rvmode_addrmap } from "@/contracts/v2/extra_logs_parsers.js";
import { polytxnlink, q_skinupdate } from "@/queries/queries.jsx";

const BurnSkinContext = createContext({});
export const useBurnSkinContext = () => useContext(BurnSkinContext);

const dead_addr = "0x000000000000000000000000000000000000dEaD";
const zero_addr = "0x0000000000000000000000000000000000000000";

const BurnButton = ({ rvsid }) => {
  return (
    <button className="flex items-center space-x-1 px-2.5 py-1 bg-orange-600/70 hover:bg-orange-600/90 text-white text-xs rounded-md transition-all duration-200">
      <Flame className="w-3 h-3" />
      <span>Burn</span>
    </button>
  );
};

// Stage 1: Ownership Validation
const OwnershipCheck = () => {
  const { rvsid, set_next_allowed, set_step, temp, set_temp } =
    useBurnSkinContext();
  const aucon = useAuthContext();

  useEffect(() => {
    checkOwnership();
  }, []);

  const checkOwnership = async () => {
    try {
      set_temp((prev) => ({ ...prev, status: "checking", error: "" }));

      // Parse RVSID
      const [rvmode, skinid_str] = rvsid.split("-");
      const skinid = parseInt(skinid_str);

      // Get contract
      const ciparams = await aucon.get_contract_init_params();
      const con_master = await DNA_SkinsMaster.get_contract(ciparams);

      // Check ownership
      const owner = await con_master.run("getTokenOwner", [rvmode, skinid]);
      const owner_lower = owner.toLowerCase();
      const wallet_addr = aucon.vault.toLowerCase();

      if (owner_lower === dead_addr.toLowerCase()) {
        throw new Error("This skin has already been burned");
      }

      if (owner_lower === zero_addr.toLowerCase()) {
        throw new Error("This skin does not exist");
      }

      if (owner_lower !== wallet_addr) {
        throw new Error("You do not own this skin");
      }

      set_temp((prev) => ({ ...prev, status: "success", owner }));
      set_next_allowed(true);

      // Auto-advance after 2 seconds
      setTimeout(() => {
        set_step(2);
      }, 2000);
    } catch (error) {
      console.error("Ownership check failed:", error);

      // Handle specific contract errors
      let error_message = "Failed to verify ownership";

      if (error.reason === "skin is burned") {
        error_message = "This skin has already been burned";
      } else if (error.message) {
        error_message = error.message;
      } else if (error.reason) {
        error_message = error.reason;
      }

      set_temp((prev) => ({
        ...prev,
        status: "error",
        error: error_message,
      }));
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        Verifying Ownership
      </h3>

      <div className="max-w-md mx-auto">
        {temp.status === "checking" && (
          <div className="space-y-4 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-300">Checking skin ownership...</p>
            <p className="text-gray-400 text-sm">Verifying #{rvsid}</p>
          </div>
        )}

        {temp.status === "success" && (
          <div className="space-y-4 text-center">
            <div className="bg-green-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">✓</span>
            </div>
            <p className="text-green-400 font-semibold">Ownership Verified!</p>
            <p className="text-gray-400 text-sm">
              You own skin #{rvsid}. Proceeding to next step...
            </p>
          </div>
        )}

        {temp.status === "error" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">!</span>
            </div>
            <p className="text-red-400 font-semibold">Verification Failed</p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
              <p className="text-red-300 text-sm">{temp.error}</p>
            </div>
            <button
              onClick={checkOwnership}
              className="bg-orange-600 hover:bg-orange-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Retry Check
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 2: Application Status Check
const ApplicationCheck = () => {
  const {
    rvsid,
    applied_map,
    on_remove_skin,
    set_next_allowed,
    set_step,
    temp,
    set_temp,
  } = useBurnSkinContext();

  useEffect(() => {
    const is_applied = applied_map[rvsid];

    if (is_applied) {
      set_next_allowed(false);
    } else {
      set_next_allowed(true);
      // Auto-advance if not applied
      setTimeout(() => {
        set_step(3);
      }, 2000);
    }
  }, []);

  const is_applied = applied_map[rvsid];

  const handleRemoveSkin = async () => {
    if (on_remove_skin) {
      try {
        set_temp((prev) => ({ ...prev, removing_skin: true }));
        await on_remove_skin(rvsid);

        // Check again after removal
        setTimeout(() => {
          const is_still_applied = applied_map[rvsid];
          set_temp((prev) => ({ ...prev, removing_skin: false }));

          if (!is_still_applied) {
            set_next_allowed(true);
            set_step(3);
          }
        }, 3000);
      } catch (error) {
        console.error("Failed to remove skin:", error);
        set_temp((prev) => ({ ...prev, removing_skin: false }));
      }
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        Application Status
      </h3>

      <div className="max-w-md mx-auto">
        {is_applied ? (
          <div className="space-y-4 text-center">
            <div className="bg-yellow-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <p className="text-yellow-400 font-semibold">
              Skin Currently Applied
            </p>
            <p className="text-gray-300 text-sm">
              This skin is currently applied to core #{is_applied}. You must
              remove it before burning.
            </p>
            <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
              <p className="text-yellow-300 text-sm mb-3">
                Please remove the skin from the core first, then return to burn
                it.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={handleRemoveSkin}
                  disabled={temp.removing_skin}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors text-sm font-semibold ${
                    temp.removing_skin
                      ? "bg-yellow-400/50 cursor-not-allowed text-white"
                      : "bg-yellow-600 hover:bg-yellow-500 text-white"
                  }`}
                >
                  {temp.removing_skin ? (
                    <>
                      <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Removing...</span>
                    </>
                  ) : (
                    <span>Remove Skin</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-center">
            <div className="bg-green-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">✓</span>
            </div>
            <p className="text-green-400 font-semibold">Ready to Burn</p>
            <p className="text-gray-400 text-sm">
              Skin is not applied to any core. Proceeding to reward
              calculation...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 3: Reward Preview
const RewardPreview = () => {
  const { rvsid, set_next_allowed, set_step, temp, set_temp } =
    useBurnSkinContext();
  const aucon = useAuthContext();

  useEffect(() => {
    calculateReward();
  }, []);

  const calculateReward = async () => {
    try {
      set_temp((prev) => ({
        ...prev,
        status: "checking",
        error: "",
        reward_sgc: null,
      }));

      // Parse RVSID
      const [rvmode, skinid_str] = rvsid.split("-");
      const skinid = parseInt(skinid_str);

      // Get contract
      const ciparams = await aucon.get_contract_init_params();
      const con_burner = await DNA_SkinsBurner.get_contract(ciparams);

      // Calculate reward
      const reward_sgc_wei = await con_burner.run("calc_sgc_reward", [
        rvmode,
        skinid,
      ]);
      const reward_sgc = parseFloat(tofeth(reward_sgc_wei));

      set_temp((prev) => ({ ...prev, status: "success", reward_sgc }));
      set_next_allowed(true);
    } catch (error) {
      console.error("Reward calculation failed:", error);
      set_temp((prev) => ({
        ...prev,
        status: "error",
        error: error.message || "Failed to calculate reward",
      }));
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        Burn Rewards
      </h3>

      <div className="max-w-md mx-auto">
        {temp.status === "checking" && (
          <div className="space-y-4 text-center">
            <div className="animate-pulse w-8 h-8 bg-orange-500 rounded-full mx-auto"></div>
            <p className="text-gray-300">Calculating SGC rewards...</p>
            <p className="text-gray-400 text-sm">
              Checking burn value for #{rvsid}
            </p>
          </div>
        )}

        {temp.status === "success" && (
          <div className="space-y-4 text-center">
            <div className="bg-orange-500 rounded-full p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <Flame className="w-8 h-8 text-white" />
            </div>
            <p className="text-orange-400 font-semibold">Burn Reward</p>
            <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-6">
              <div className="text-4xl font-bold text-white mb-2">
                {temp.reward_sgc?.toFixed(2)} SGC
              </div>
              <p className="text-gray-400 text-sm">
                You will receive this amount when you burn #{rvsid}
              </p>
            </div>
            <p className="text-gray-300 text-sm">
              This reward is calculated based on the skin's rarity and
              attributes.
            </p>
          </div>
        )}

        {temp.status === "error" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">!</span>
            </div>
            <p className="text-red-400 font-semibold">Calculation Failed</p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
              <p className="text-red-300 text-sm">{temp.error}</p>
            </div>
            <button
              onClick={calculateReward}
              className="bg-orange-600 hover:bg-orange-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Retry Calculation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 4: NFT Approval Check
const ApprovalCheck = () => {
  const { rvsid, set_next_allowed, set_step, temp, set_temp } =
    useBurnSkinContext();
  const aucon = useAuthContext();

  useEffect(() => {
    checkApproval();
  }, []);

  const checkApproval = async () => {
    try {
      set_temp((prev) => ({
        ...prev,
        status: "checking",
        error: "",
        is_approved: false,
      }));

      // Parse RVSID
      const [rvmode, skinid_str] = rvsid.split("-");

      // Get contract addresses
      const wallet_addr = aucon.vault;
      const payrouter_addr = contractAddress_list.dnapayrouter;
      const skin_contract_addr = skins_rvmode_addrmap[rvmode];

      // Get ERC721 contract
      const ciparams = await aucon.get_contract_init_params();
      const erc721con = await ERC721base.get_contract(
        skin_contract_addr,
        ciparams,
      );

      // Check approval
      const is_approved = await erc721con.run("isApprovedForAll", [
        wallet_addr,
        payrouter_addr,
      ]);

      set_temp((prev) => ({ ...prev, status: "success", is_approved }));

      if (is_approved) {
        set_next_allowed(true);
        // Auto-advance if already approved
        setTimeout(() => {
          set_step(5);
        }, 2000);
      } else {
        set_next_allowed(false);
      }
    } catch (error) {
      console.error("Approval check failed:", error);
      set_temp((prev) => ({
        ...prev,
        status: "error",
        error: error.message || "Failed to check approval",
      }));
    }
  };

  const handleApprove = async () => {
    try {
      set_temp((prev) => ({ ...prev, approval_status: "processing" }));

      // Parse RVSID
      const [rvmode, skinid_str] = rvsid.split("-");

      // Get contract addresses
      const payrouter_addr = contractAddress_list.dnapayrouter;
      const skin_contract_addr = skins_rvmode_addrmap[rvmode];

      // Get ERC721 contract
      const ciparams = await aucon.get_contract_init_params();
      const erc721con = await ERC721base.get_contract(
        skin_contract_addr,
        ciparams,
      );

      // Set approval
      const tx = await erc721con.run("setApprovalForAll", [
        payrouter_addr,
        true,
      ]);

      set_temp((prev) => ({ ...prev, approval_status: "success" }));
      set_next_allowed(true);
    } catch (error) {
      console.error("Approval failed:", error);
      set_temp((prev) => ({
        ...prev,
        approval_status: "error",
        approval_error: error.message || "Failed to approve",
      }));
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        NFT Approval Check
      </h3>

      <div className="max-w-md mx-auto">
        {temp.status === "checking" && (
          <div className="space-y-4 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-300">Checking NFT approval...</p>
          </div>
        )}

        {temp.status === "success" && temp.is_approved && (
          <div className="space-y-4 text-center">
            <div className="bg-green-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">✓</span>
            </div>
            <p className="text-green-400 font-semibold">Approval Confirmed</p>
            <p className="text-gray-400 text-sm">
              NFT is approved for burning. Proceeding to final confirmation...
            </p>
          </div>
        )}

        {temp.status === "success" && !temp.is_approved && (
          <div className="space-y-4 text-center">
            <div className="bg-yellow-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <p className="text-yellow-400 font-semibold">Approval Required</p>
            <p className="text-gray-300 text-sm mb-4">
              You need to approve the burn contract to access your NFT.
            </p>

            {temp.approval_status === "processing" ? (
              <div className="space-y-2">
                <div className="animate-pulse w-6 h-6 bg-orange-500 rounded-full mx-auto"></div>
                <p className="text-orange-400">Approving...</p>
              </div>
            ) : temp.approval_status === "success" ? (
              <div className="space-y-2">
                <div className="bg-green-500 rounded-full p-1 w-8 h-8 mx-auto flex items-center justify-center">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <p className="text-green-400">Approved!</p>
              </div>
            ) : (
              <button
                onClick={handleApprove}
                className="bg-orange-600 hover:bg-orange-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Approve NFT Access
              </button>
            )}

            {temp.approval_error && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                <p className="text-red-300 text-sm">{temp.approval_error}</p>
              </div>
            )}
          </div>
        )}

        {temp.status === "error" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">!</span>
            </div>
            <p className="text-red-400 font-semibold">Check Failed</p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
              <p className="text-red-300 text-sm">{temp.error}</p>
            </div>
            <button
              onClick={checkApproval}
              className="bg-orange-600 hover:bg-orange-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Retry Check
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 5: Final Confirmation
const FinalConfirmation = () => {
  const { rvsid, skin_name, skin_rarity, temp, set_next_allowed } =
    useBurnSkinContext();

  useEffect(() => {
    set_next_allowed(true);
  }, []);

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        ⚠️ Final Confirmation
      </h3>

      <div className="max-w-md mx-auto space-y-6">
        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <span className="text-red-400 font-semibold">
              Warning: Irreversible Action
            </span>
          </div>
          <p className="text-gray-300 text-sm">
            This burn action cannot be reversed. Your skin NFT will be
            permanently destroyed.
          </p>
        </div>

        <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3">Burn Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Skin:</span>
              <span className="text-white">{skin_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">RVSID:</span>
              <span className="text-white">#{rvsid}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Rarity:</span>
              <span className="text-white">{skin_rarity}</span>
            </div>
            <div className="border-t border-gray-600 pt-2 mt-3">
              <div className="flex justify-between">
                <span className="text-gray-400">SGC Reward:</span>
                <span className="text-orange-400 font-semibold">
                  {temp.reward_sgc?.toFixed(2)} SGC
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-gray-400 text-sm mb-4">
            Please proceed with caution. This action will permanently destroy
            your skin NFT.
          </p>
          <p className="text-orange-400 text-xs">
            Click "Burn Skin" to continue with the transaction.
          </p>
        </div>
      </div>
    </div>
  );
};

// Stage 6: Transaction Execution
const TransactionExecution = () => {
  const { rvsid, post_burn_function, set_next_allowed, set_step, temp, set_temp } =
    useBurnSkinContext();
  const aucon = useAuthContext();

  useEffect(() => {
    set_next_allowed(false);
  }, []);

  const executeBurn = async () => {
    try {
      set_temp((prev) => ({
        ...prev,
        tx_status: "processing",
        tx_error: "",
        tx_hash: "",
      }));

      // Parse RVSID
      const [rvmode, skinid_str] = rvsid.split("-");
      const skinid = parseInt(skinid_str);

      // Get contract
      const ciparams = await aucon.get_contract_init_params();
      const con_burner = await DNA_SkinsBurner.get_contract(ciparams);

      // Execute burn
      const tx = await con_burner.run("burnSkin", [rvmode, skinid], {
        wait: false,
      });
      const tx_hash = tx.hash;

      set_temp((prev) => ({ ...prev, tx_hash, tx_status: "confirming" }));

      // Wait for confirmation
      const receipt = await con_burner.wait(tx);

      set_temp((prev) => ({
        ...prev,
        tx_status: "success",
        tx_receipt: receipt,
      }));
      set_next_allowed(true);

      // Call q_skinupdate to refresh skin data after burn
      try {
        const updateQuery = q_skinupdate({ rvmode, skinid });
        await updateQuery.queryFn();
      } catch (updateError) {
        console.warn("Failed to update skin data after burn:", updateError);
        // Don't fail the burn process if update fails
      }

      // Call post_burn_function to refresh all SkinsMaster data
      if (post_burn_function) {
        try {
          await post_burn_function();
        } catch (postBurnError) {
          console.warn("Failed to refresh UI data after burn:", postBurnError);
        }
      }

      // Auto-advance to results
      setTimeout(() => {
        set_step(7);
      }, 2000);
    } catch (error) {
      console.error("Burn transaction failed:", error);
      set_temp((prev) => ({
        ...prev,
        tx_status: "error",
        tx_error: error.message || "Transaction failed",
      }));
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        Burn Transaction
      </h3>

      <div className="max-w-md mx-auto">
        {!temp.tx_status && (
          <div className="space-y-4 text-center">
            <div className="bg-orange-500 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
              <Flame className="w-10 h-10 text-white" />
            </div>
            <p className="text-white font-semibold">Ready to Burn</p>
            <p className="text-gray-400 text-sm mb-6">
              Click the button below to initiate the burn transaction.
            </p>
            <button
              onClick={executeBurn}
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              🔥 Burn Skin
            </button>
          </div>
        )}

        {temp.tx_status === "processing" && (
          <div className="space-y-4 text-center">
            <div className="animate-pulse w-12 h-12 bg-orange-500 rounded-full mx-auto flex items-center justify-center">
              <Flame className="w-6 h-6 text-white animate-bounce" />
            </div>
            <p className="text-orange-400 font-semibold">Initiating Burn...</p>
            <p className="text-gray-400 text-sm">
              Please confirm the transaction in your wallet
            </p>
          </div>
        )}

        {temp.tx_status === "confirming" && (
          <div className="space-y-4 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-orange-400 font-semibold">Burning Skin...</p>
            {temp.tx_hash && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                <p className="text-gray-400 text-xs">Transaction Hash:</p>
                <p className="text-gray-300 text-sm font-mono break-all">
                  {temp.tx_hash}
                </p>
              </div>
            )}
            <p className="text-gray-400 text-sm">
              Please wait while we confirm the burn transaction
            </p>
          </div>
        )}

        {temp.tx_status === "success" && (
          <div className="space-y-4 text-center">
            <div className="bg-green-500 rounded-full p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <span className="text-white font-bold text-2xl">✓</span>
            </div>
            <p className="text-green-400 font-semibold text-lg">
              Burn Successful!
            </p>
            <p className="text-gray-400 text-sm">Processing burn results...</p>
          </div>
        )}

        {temp.tx_status === "error" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <span className="text-white font-bold text-xl">!</span>
            </div>
            <p className="text-red-400 font-semibold">Transaction Failed</p>
            {temp.tx_error && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                <p className="text-gray-400 text-xs">Error:</p>
                <p className="text-red-300 text-sm">{temp.tx_error}</p>
              </div>
            )}
            <button
              onClick={executeBurn}
              className="bg-red-600 hover:bg-red-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Retry Burn
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 7: Success & Results
const BurnResults = () => {
  const { rvsid, temp, handle_close } = useBurnSkinContext();
  const [burn_event, set_burn_event] = useState(null);

  useEffect(() => {
    if (temp.tx_receipt) {
      parseBurnEvent();
    }
  }, [temp.tx_receipt]);

  const parseBurnEvent = async () => {
    try {
      // Parse logs from transaction receipt
      const logs = temp.tx_receipt.parsed_logs || [];
      const skin_burned_event = logs.find((log) => log.name === "SkinBurned");

      if (skin_burned_event) {
        set_burn_event(skin_burned_event.args);
      }
    } catch (error) {
      console.error("Failed to parse burn event:", error);
    }
  };

  const handleViewTransaction = () => {
    if (temp.tx_hash) {
      window.open(polytxnlink(temp.tx_hash), "_blank");
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        🎉 Burn Complete!
      </h3>

      <div className="max-w-md mx-auto space-y-6">
        <div className="text-center">
          <div className="bg-green-500 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center mb-4">
            <Flame className="w-10 h-10 text-white" />
          </div>
          <p className="text-green-400 font-semibold text-lg mb-2">
            Skin Successfully Burned!
          </p>
          <p className="text-gray-400 text-sm">
            Your skin #{rvsid} has been permanently destroyed.
          </p>
        </div>

        {burn_event && (
          <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-3">Burn Rewards</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">SGC Received:</span>
                <span className="text-green-400 font-bold">
                  {burn_event.reward_sgc?.toFixed(2)} SGC
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Vehicle Type:</span>
                <span className="text-white">{burn_event.rvmode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Token ID:</span>
                <span className="text-white">{burn_event.tokenid}</span>
              </div>
            </div>
          </div>
        )}

        {temp.tx_hash && (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
            <h4 className="text-white font-semibold mb-2">
              Transaction Details
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Transaction:</span>
                <button
                  onClick={handleViewTransaction}
                  className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-sm"
                >
                  <span>View on Chain</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <p className="text-gray-300 text-xs font-mono break-all">
                {temp.tx_hash}
              </p>
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={handle_close}
            className="bg-purple-600 hover:bg-purple-500 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const BurnSkinModal = ({
  trigger = <BurnButton />,
  rvsid,
  skin_name = "",
  skin_rarity = "",
  applied_map = {},
  on_remove_skin,
  post_burn_function,
  on_open,
  on_close,
}) => {
  const [open, set_open] = useState(false);
  const [step, set_step] = useState(1);
  const [next_allowed, set_next_allowed] = useState(false);
  const [temp, set_temp] = useState({});

  const handle_open = () => {
    set_open(true);
    set_step(1);
    set_next_allowed(false);
    set_temp({});
    if (on_open) on_open();
  };

  const handle_close = () => {
    set_open(false);
    set_step(1);
    set_next_allowed(false);
    set_temp({});
    if (on_close) on_close();
  };

  const burncon = {
    step,
    set_step,
    next_allowed,
    set_next_allowed,
    handle_close,
    rvsid,
    skin_name,
    skin_rarity,
    applied_map,
    on_remove_skin,
    post_burn_function,
    temp,
    set_temp,
  };

  const render_current_stage = () => {
    switch (step) {
      case 1:
        return <OwnershipCheck />;
      case 2:
        return <ApplicationCheck />;
      case 3:
        return <RewardPreview />;
      case 4:
        return <ApprovalCheck />;
      case 5:
        return <FinalConfirmation />;
      case 6:
        return <TransactionExecution />;
      case 7:
        return <BurnResults />;
      default:
        return <OwnershipCheck />;
    }
  };

  const get_stage_title = () => {
    const titles = {
      1: "Ownership Check",
      2: "Application Status",
      3: "Reward Preview",
      4: "NFT Approval",
      5: "Final Confirmation",
      6: "Burn Transaction",
      7: "Burn Results",
    };
    return titles[step] || "Burn Skin";
  };

  return (
    <BurnSkinContext.Provider value={burncon}>
      {/* Trigger Element */}
      <div onClick={handle_open} className="cursor-pointer">
        {trigger}
      </div>

      {/* Modal */}
      <Modal
        is_open={open}
        on_close={handle_close}
        title={`${get_stage_title()} - Step ${step} of 7`}
        size="lg"
        backdrop_close={step < 6} // Prevent close during transaction
        className="bg-gray-900/95 backdrop-blur-md"
      >
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {render_current_stage()}
        </div>

        {/* Navigation Footer */}
        <div className="sticky bottom-0 border-t border-gray-800 p-6 flex justify-between bg-gray-900/95 backdrop-blur-md">
          <button
            onClick={() => {
              if (step > 1 && step < 6) {
                set_step(step - 1);
              }
            }}
            disabled={step === 1 || step >= 6}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              step === 1 || step >= 6
                ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                : "bg-gray-700 text-white hover:bg-gray-600"
            }`}
          >
            Previous
          </button>

          <div className="text-gray-400 text-sm">Step {step} of 7</div>

          <button
            onClick={() => {
              if (step === 7) {
                handle_close();
              } else if (step === 6) {
                // Transaction stage - button handled by component
                return;
              } else if (next_allowed && step < 7) {
                set_step(step + 1);
              }
            }}
            disabled={!next_allowed && step < 6}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              step === 6
                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                : step === 7
                  ? "bg-purple-600 text-white hover:bg-purple-500"
                  : next_allowed
                    ? "bg-orange-600 text-white hover:bg-orange-500"
                    : "bg-gray-600 text-gray-400 cursor-not-allowed"
            }`}
          >
            {step === 7 ? "Close" : step === 6 ? "Processing..." : "Next"}
          </button>
        </div>
      </Modal>
    </BurnSkinContext.Provider>
  );
};
